/**
 * APIレスポンスの変換ユーティリティ
 * バックエンド（snake_case）とフロントエンド（camelCase）間の変換を一元管理
 */

import type {
  AgeGroup,
  ChatEvent,
  ChatMessage,
  ChatMessageRecord,
  ImageRecord,
  AttachedFileInfo,
  AttachedFileInfoFromBackend,
} from '../types';

/**
 * 生成画像の型定義
 */
export interface GeneratedImage {
  id: string;
  prompt: string;
  image_url?: string;
  image_base64?: string;
  timestamp: number;
  filtered: boolean;
  error?: string;
}

/**
 * バックエンドの添付ファイル情報をフロントエンド形式に変換
 */
export function transformAttachedFile(file: AttachedFileInfoFromBackend): AttachedFileInfo {
  return {
    file_id: file.file_id,
    filename: file.filename,
    mimeType: file.mime_type,
  };
}

/**
 * バックエンドの添付ファイル情報配列をフロントエンド形式に変換
 */
export function transformAttachedFiles(
  files?: AttachedFileInfoFromBackend[]
): AttachedFileInfo[] | undefined {
  if (!files || files.length === 0) {
    return undefined;
  }
  return files.map(transformAttachedFile);
}

function buildEventFromRecord(msg: ChatMessageRecord): ChatEvent | undefined {
  if (!msg.event_type) return undefined;

  return {
    type: msg.event_type,
    params: {
      block_category: msg.filtering_result?.block_category ?? null,
    },
  };
}

/**
 * バックエンドのチャットメッセージレコードをフロントエンド形式に変換
 * 1つのレコードからユーザーメッセージとAI応答の2つのChatMessageを生成
 */
export function transformChatMessageRecord(msg: ChatMessageRecord): ChatMessage[] {
  const messages: ChatMessage[] = [];
  const convertedAttachedFiles = transformAttachedFiles(msg.attached_files);

  // ユーザーメッセージ
  if (msg.user_input) {
    messages.push({
      id: `${msg.id}-user`,
      session_id: msg.session_id,
      user_input: msg.user_input,
      ai_response: '',
      timestamp: msg.timestamp,
      age_group: msg.age_group as AgeGroup,
      isUser: true,
      attachedFiles: convertedAttachedFiles,
    });
  }

  // AI応答
  const isBlocked = msg.filtering_result?.result === 'block';
  const event = buildEventFromRecord(msg);

  if (msg.ai_response || event) {
    messages.push({
      id: msg.id,
      session_id: msg.session_id,
      user_input: msg.user_input,
      ai_response: msg.ai_response,
      timestamp: msg.timestamp,
      age_group: msg.age_group as AgeGroup,
      isUser: false,
      isError: isBlocked,
      sources: msg.sources,
      event,
    });
  }

  return messages;
}

/**
 * バックエンドのチャットメッセージレコード配列を変換
 */
export function transformChatMessages(records: ChatMessageRecord[]): ChatMessage[] {
  return records.flatMap(transformChatMessageRecord);
}

/**
 * バックエンドの画像レコードをフロントエンド形式に変換
 * timestamp を秒からミリ秒に変換
 */
export function transformImageRecord(img: ImageRecord): GeneratedImage {
  return {
    id: img.image_id || img.id,
    prompt: img.prompt,
    image_url: img.image_url,
    image_base64: img.image_base64,
    timestamp: img.timestamp * 1000, // 秒からミリ秒に変換
    filtered: img.filtered || false,
    error: img.error,
  };
}

/**
 * バックエンドの画像レコード配列を変換
 */
export function transformImageRecords(records: ImageRecord[]): GeneratedImage[] {
  return records.map(transformImageRecord);
}
